import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchTeamMembers, fetchTimelineEvents } from '../lib/cms';
import { Shield, Users, Calendar, Award, CheckCircle2, ChevronRight, FileText, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';

export default function About() {
  const [team, setTeam] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleMilestones, setVisibleMilestones] = useState(20); // Show first 20 milestones initially

  const values = [
    {
      title: "Kvalitet",
      desc: "Vi går aldrig på kompromis med kvaliteten af vores arbejde og materialer. Alt udføres efter højeste standarder."
    },
    {
      title: "Ordentlighed",
      desc: "Vi overholder alle aftaler, rydder pænt op efter os selv og udfører arbejdet efter gældende lovgivning."
    },
    {
      title: "Personlig service",
      desc: "Du får altid en personlig og nærværende betjening, hvor vi lytter til dine specifikke ønsker og behov."
    },
    {
      title: "ISO-certificeret",
      desc: "Som ISO-certificeret virksomhed sikrer vi ensartet høj kvalitet og fuld sporbarhed i alle vores processer."
    }
  ];

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const [teamData, timelineData] = await Promise.all([
        fetchTeamMembers(),
        fetchTimelineEvents(),
      ]);
      setTeam(teamData);
      setTimeline(timelineData);
      setLoading(false);
    }
    loadData();
  }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Forside",
        "item": "https://detekniq.dk/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Om os",
        "item": "https://detekniq.dk/om-os"
      }
    ]
  };

  return (
    <div className="bg-white">
      <SEO
        title="Om virksomheden"
        description="Læs historien om D.E. TekniQ K/S fra 1954 til i dag. Mød vores erfarne medarbejdere Kaj, Dorit, Henrik og Morten. Din lokale el-specialist i Midtjylland."
        schema={aboutSchema}
      />
      {/* Header Banner */}
      <div className="bg-navy-950 py-16 text-white md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-energy-400">Hvem er vi?</span>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl mt-3">
            Om D.E. TekniQ K/S
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-navy-200">
            Læs historien om vores ISO-certificerede el- og alarmvirksomhed, mød vores dygtige medarbejdere og hør om vores værdier.
          </p>
        </div>
      </div>

      {/* Presentation & Values */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Vores Fundament</span>
              <h2 className="mt-3 text-3xl font-bold text-navy-950 sm:text-4xl">
                Professionelle strømninger og god service siden 1954
              </h2>
              <p className="mt-6 text-base text-graphite-700 leading-relaxed">
                D.E. TekniQ K/S har rødder helt tilbage til 1954, hvor virksomheden blev grundlagt i Demstrup. I dag er vi beliggende i Kjellerup, hvorfra vores kørende servicevogne dækker det meste af Midtjylland.
              </p>
              <p className="mt-4 text-base text-graphite-700 leading-relaxed">
                Vi går utroligt langt for at gøre vores kunder tilfredse, og vi bygger vores arbejde på et solidt fundament af kvalitet, ordentlighed og personlig service.
              </p>

              {/* YouTube Video right under presentation text */}
              <div className="mt-8 relative aspect-video w-full shadow-lg border border-navy-100">
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src="https://www.youtube.com/embed/6qw9ZOriy-k?si=TAXLTiF6dQVXS1Fy"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {values.map((val, idx) => (
                <div key={idx} className="rounded-none border border-navy-100 bg-navy-50/30 p-6 shadow-sm">
                  <CheckCircle2 className="h-6 w-6 text-energy-600" />
                  <h3 className="mt-4 font-bold text-navy-950">{val.title}</h3>
                  <p className="mt-2 text-xs text-graphite-600 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Certificeringer & Rapporter */}
          <div className="mt-16 border-t border-navy-100 pt-12">
            <div className="max-w-3xl">
              <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Dokumentation & Standarder</span>
              <h3 className="mt-2 text-2xl font-bold text-navy-950">
                Certifikater og rapporter
              </h3>
              <p className="mt-2 text-sm text-graphite-600">
                Vi lægger vægt på gennemsigtighed, kvalitetssikring og bæredygtig udvikling. Hent eller læs vores officielle dokumenter herunder.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <a
                href="/_assets/certifikat-248-2-2026-2029-6289.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col justify-between border border-navy-100 bg-navy-50/40 p-6 transition-all duration-200 hover:border-energy-500 hover:bg-white hover:shadow-md"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-none bg-energy-100 text-energy-700 group-hover:bg-energy-600 group-hover:text-white transition-colors">
                      <FileText className="h-5 w-5" />
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-navy-600 group-hover:text-energy-600">
                      PDF <ExternalLink className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  <h4 className="mt-4 text-lg font-bold text-navy-950 group-hover:text-energy-600 transition-colors">
                    ISO 9001 Certifikat
                  </h4>
                  <p className="mt-2 text-xs text-graphite-600 leading-relaxed">
                    Certifikat 248-2 (2026–2029). Kvalitetsledelsessystem for D.E. TekniQ K/S inden for el- og alarminstallationer.
                  </p>
                </div>
                <div className="mt-6 flex items-center gap-1.5 text-xs font-bold text-energy-700">
                  <span>Åbn certifikat i nyt vindue</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </a>

              <a
                href="/_assets/vsme-esg-rapport-2025-2.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col justify-between border border-navy-100 bg-navy-50/40 p-6 transition-all duration-200 hover:border-energy-500 hover:bg-white hover:shadow-md"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-none bg-energy-100 text-energy-700 group-hover:bg-energy-600 group-hover:text-white transition-colors">
                      <FileText className="h-5 w-5" />
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-navy-600 group-hover:text-energy-600">
                      PDF <ExternalLink className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  <h4 className="mt-4 text-lg font-bold text-navy-950 group-hover:text-energy-600 transition-colors">
                    VSME ESG Rapport 2025
                  </h4>
                  <p className="mt-2 text-xs text-graphite-600 leading-relaxed">
                    Vores bæredygtigheds- og ESG-rapportering i henhold til VSME-standarden for 2025.
                  </p>
                </div>
                <div className="mt-6 flex items-center gap-1.5 text-xs font-bold text-energy-700">
                  <span>Åbn rapport i nyt vindue</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="bg-navy-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Mød Teamet</span>
            <h2 className="mt-3 text-3xl font-bold text-navy-950 sm:text-4xl">
              Vores erfarne medarbejdere
            </h2>
            <p className="mt-4 text-base text-graphite-600">
              Hos D.E. TekniQ er vi 4 flittige medarbejdere, der arbejder på tværs af vores fokusområder for at give dig den bedste service.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {team.map((member) => (
                <div key={member.id} className="bg-white border border-navy-100 p-8 shadow-sm flex flex-col items-center text-center justify-between">
                  <div className="flex flex-col items-center">
                    {/* Round Image Container */}
                    <div className="h-36 w-36 rounded-full overflow-hidden relative border-4 border-energy-50 shadow-inner mb-6">
                      {member.image && member.image[0] ? (
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=80,format=auto/${member.image[0].replace('https://assets.auxodev.dk/', '')}`}
                          alt={member.name}
                          className="h-full w-full object-cover rounded-full"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-navy-900 text-white rounded-full">
                          <Users className="h-12 w-12 text-energy-500" />
                        </div>
                      )}
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-energy-600">
                      {member.role?.da || member.role}
                    </span>
                    <h3 className="mt-2 text-xl font-bold text-navy-950">{member.name}</h3>
                  </div>
                  <div className="w-full mt-6">
                    <Link
                      to={`/om-os/medarbejder/${member.id}`}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-none bg-energy-50 hover:bg-energy-600 px-4 py-2.5 text-xs font-bold text-energy-800 hover:text-white transition-all duration-200 border border-energy-200/60"
                    >
                      Læs mere <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* History Timeline Section */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-energy-600">Vores Historie</span>
            <h2 className="mt-3 text-3xl font-bold text-navy-950 sm:text-4xl">
              Tidslinje: Fra 1954 til i dag
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="relative border-l border-navy-100 ml-4 md:ml-32 space-y-12">
              {timeline.slice(0, visibleMilestones).map((event) => (
                <div key={event.id} className="relative pl-8 md:pl-12 group">
                  {/* Year Bubble (Desktop) */}
                  <div className="absolute -left-4 top-1.5 md:-left-36 md:w-28 text-right hidden md:block">
                    <span className="text-lg font-extrabold text-energy-600 bg-energy-50 px-3 py-1 rounded-none">
                      {event.year}
                    </span>
                  </div>

                  {/* Dot on timeline */}
                  <div className="absolute -left-1.5 top-2.5 h-3 w-3 rounded-none bg-energy-600 ring-4 ring-white transition-all duration-300 group-hover:scale-125" />

                  {/* Year (Mobile) */}
                  <div className="md:hidden mb-2">
                    <span className="text-sm font-extrabold text-energy-600 bg-energy-50 px-2.5 py-1 rounded-none">
                      {event.year}
                    </span>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-lg font-bold text-navy-950 group-hover:text-energy-600 transition-colors">
                      {event.title?.da || event.title}
                    </h3>
                    <div
                      className="mt-2 text-sm text-graphite-600 leading-relaxed prose"
                      dangerouslySetInnerHTML={{ __html: event.description?.da || event.description }}
                    />
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {timeline.length > visibleMilestones && (
                <div className="pl-8 md:pl-12 pt-4">
                  <button
                    onClick={() => setVisibleMilestones(prev => prev + 5)}
                    className="inline-flex items-center gap-1.5 rounded-none bg-navy-50 px-5 py-2.5 text-sm font-semibold text-navy-950 hover:bg-navy-100 transition-colors focus:outline-none focus:ring-2 focus:ring-energy-500"
                  >
                    Vis flere milepæle <ChevronRight className="h-4 w-4 rotate-90" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
